import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { requestPasswordReset } from '../api';

const BRAND = {
  bone: '#F0F0F0',
  cobalt: '#5A1DEF',
  slate: '#404041',
} as const;

export function PasswordRecoveryPage() {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage(null);
    setError(null);

    try {
      await requestPasswordReset(email.trim());
      setMessage(t('auth.passwordRecoverySuccess'));
    } catch {
      setError(t('auth.passwordRecoveryError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: BRAND.bone }}
    >
      <div className="mb-8 text-center">
        <img
          src="https://storage.googleapis.com/atek-public-assets/brand/logos/atek-logo-main-dark-medium.png"
          alt="ATEK"
          className="h-16 w-auto mx-auto mb-3"
        />
        <p className="text-sm tracking-wide" style={{ color: BRAND.slate }}>
          {t('auth.tagline')}
        </p>
      </div>

      <Card className="w-full max-w-sm shadow-lg border-0">
        <CardContent className="pt-8 pb-8 px-8">
          <Link
            to="/login"
            className="mb-6 inline-flex items-center gap-2 text-sm hover:underline"
            style={{ color: BRAND.cobalt }}
          >
            <ArrowLeft className="h-4 w-4" />
            {t('auth.backToSignIn')}
          </Link>

          <div className="mb-6">
            <div
              className="mb-4 flex h-10 w-10 items-center justify-center rounded-full"
              style={{ backgroundColor: 'rgba(90, 29, 239, 0.10)', color: BRAND.cobalt }}
            >
              <Mail className="h-5 w-5" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">
              {t('auth.passwordRecoveryTitle')}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('auth.passwordRecoveryDescription')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {message && (
              <Alert>
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="recovery-email">{t('auth.email')}</Label>
              <Input
                id="recovery-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
                disabled={isSubmitting}
              />
            </div>

            <Button
              type="submit"
              className="w-full text-white font-medium"
              style={{ backgroundColor: BRAND.cobalt }}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
